import { Trans, useLingui } from '@lingui/react/macro';
import { Typography } from '@remarkable/ark-web';
import { useLocation } from '@tanstack/react-router';
import { ComponentLocations } from 'src/analytics/trackingTypes';

import type { Subscription } from 'src/api/endpoints/storeApi.types';
import { NotificationBox } from 'src/components';
import { Link2 } from 'src/components/Link2';
import { formatDate } from 'src/utils/formatDate';
import { subscriptionHelpers } from 'src/utils/subscriptionHelpers';
import { URLS } from 'src/utils/urls/urls';

interface PausedSubscriptionNotificationProps {
  subscription?: Subscription;
}

export const PausedSubscriptionNotification = ({
  subscription,
}: PausedSubscriptionNotificationProps) => {
  const { t, i18n } = useLingui();
  const { pathname } = useLocation();
  if (
    subscriptionHelpers.isFreeConnect(subscription) ||
    !subscriptionHelpers.isDeactivated(subscription)
  ) {
    return null;
  }

  if (subscription.access.canChangePaymentInformation) {
    const cancellationDate = formatDate(
      subscription.paymentInformation.cancellationAt,
      {
        locale: i18n.locale,
      }
    );
    return (
      <NotificationBox
        variant="error"
        title={t`Your subscription is on hold`}
        className="mb-32"
      >
        <Typography variant="body-sm" as="p">
          <Trans>
            There was an error with your payment, and your subscription is on
            hold. Your team can still access their files, but subscription
            benefits are unavailable.
          </Trans>{' '}
          <br />
          <Trans>
            To restore access, please change your card or update your payment
            details. Your subscription will be canceled{' '}
            <Typography as="span" variant="body-sm" fontWeight="bold">
              {cancellationDate}
            </Typography>
            , if you don&apos;t update your payment details.
          </Trans>
        </Typography>
        {pathname === URLS.SUBSCRIPTION_PAYMENT ? null : (
          <Link2
            inline
            className="mt-8"
            to="/subscriptions/payment"
            componentLocation={ComponentLocations.MANAGE_CONNECT.PAYMENT_PAGE}
          >
            <Trans>Update payment</Trans>
          </Link2>
        )}
      </NotificationBox>
    );
  }
  return (
    <NotificationBox
      variant="warning"
      title={t`Your company's subscription is on hold`}
      className="mb-32"
    >
      <Typography variant="body-sm" as="p">
        <Trans>
          Your subscription features are unavailable, but you can still access
          your files. Please ask your admin to update their payment details.
        </Trans>
      </Typography>
    </NotificationBox>
  );
};
